import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, AlertCircle, Eye, EyeOff, HelpCircle, ArrowLeft, Clock } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [idleMessage, setIdleMessage] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('reason') === 'idle') {
      setIdleMessage(true);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const userData = await login(email, password);
      if (!userData.password_initialized) {
        navigate('/set-password');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0776BD] via-[#055A92] to-[#044A7A] flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img
              src="/mr-electric-logo.jpg"
              alt="Mr. Electric of Southwest Las Vegas"
              className="h-24 w-auto object-contain rounded-lg bg-white p-2 shadow-lg"
              data-testid="login-logo"
            />
          </div>
          <p className="text-white/70 mt-1">Inventory Control</p>
        </div>

        <Card className="border-0 shadow-xl rounded-xl">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="font-heading text-2xl tracking-tight text-[#1F2937]">Sign In</CardTitle>
            <CardDescription className="text-[#6B7280]">Enter your credentials to access the inventory system</CardDescription>
          </CardHeader>
          <CardContent>
            {idleMessage && (
              <Alert className="mb-4 rounded-lg border-amber-200 bg-amber-50">
                <Clock className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-700">
                  You were signed out due to inactivity. Please sign in again.
                </AlertDescription>
              </Alert>
            )}
            {error && (
              <Alert variant="destructive" className="mb-4 rounded-lg">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-[#1F2937]">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@mrelectric.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="h-12 rounded-lg border-gray-300 focus:border-[#0776BD] focus:ring-[#0776BD]/20"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium text-[#1F2937]">Password</Label>
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-xs text-[#0776BD] hover:text-[#055A92] hover:underline font-medium"
                    data-testid="forgot-password-link"
                  >
                    Forgot Password?
                  </button>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                    className="h-12 rounded-lg border-gray-300 focus:border-[#0776BD] focus:ring-[#0776BD]/20 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#1F2937] transition-colors"
                    tabIndex={-1}
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-[#0776BD] hover:bg-[#055A92] text-white font-semibold rounded-lg shadow-sm transition-all"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing In...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            <div className="mt-5 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => navigate('/login-help')}
                className="w-full flex items-center justify-center gap-2 text-sm text-[#6B7280] hover:text-[#0776BD] transition-colors py-2"
                data-testid="login-help-link"
              >
                <HelpCircle size={16} />
                Need help signing in?
              </button>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-white/50 text-sm mt-6">
          Mr. Electric of Southwest Las Vegas
        </p>
      </div>
    </div>
  );
};

export default Login;
